import {
  Flex,
  Text,
  IconButton,
  Icon,
  TableContainer,
  Table,
  Tbody,
  Tr,
  Td,
} from "@chakra-ui/react";
import LoadingSpinner from "components/loadingSpinner/LoadingSpinner";
import Card from "components/card/Card";
import { format, parseISO } from "date-fns";
import { useEffect, useState, useMemo } from "react";
import { buscarBloqueioConta } from "config/apis/requests";
import { IsEmpty } from "config/functions";
import {
  MdLock,
  MdLockOpen,
  MdBlock,
  MdCheckCircle,
} from "react-icons/md";

interface HistoricoItem {
  descricao: string;
  produto: string;
  data: string;
  login: string;
  justificativa: string;
  tipoAcao: 'BLOQUEIO' | 'DESBLOQUEIO';
  descricaoMotivoAcao?: string;
  idMotivoAcao?: number;
}

export default function HistoricoBloqueioConta(props: { dataUser: any; idProduto: number }) {
  const [dadosBloqueio, setDadosBloqueio] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setDadosBloqueio(null); // Limpar o histórico imediatamente quando dataUser mudar
    const fetchHistorico = async () => {
      const numeroDocumento = Array.isArray(props.dataUser) 
        ? props.dataUser[0]?.numeroDocumento 
        : props.dataUser?.numeroDocumento;

      if (IsEmpty(numeroDocumento)) {
        setDadosBloqueio(null);
        return;
      }
      setLoading(true);
      try {
        const res = await buscarBloqueioConta(numeroDocumento);
        if (typeof res === 'object') {
          setDadosBloqueio(res);
        } else {
          setDadosBloqueio(null);
        }
      } catch (error) {
        console.error("Erro ao buscar histórico de bloqueios:", error);
        setDadosBloqueio(null);
      } finally {
        setLoading(false);
      }
    };

    fetchHistorico();
  }, [props.dataUser]);

  const historicoFiltrado = useMemo(() => {
    if (!dadosBloqueio) return [];

    let items: HistoricoItem[] = [];

    // Processar contas normais e gratuidades
    const todasContas = [
      ...(dadosBloqueio.contas || []),
      ...(dadosBloqueio.contasGratuidades || []).map((cg: any) => ({
        ...cg,
        id: cg.idContaGratuidade,
        idProduto: cg.idProduto ?? 3, // Gratuidades costumam ser produto 3
      }))
    ];

    const keys = new Set<string>();

    todasContas.forEach((conta: any) => {
      // Determinar o idProduto da conta
      let idProdutoConta = conta.idProduto;
      
      // Se for idProduto !== 3 e tiver id no formato X.X.X.X.P, extrair P
      if (idProdutoConta !== 3 && conta.id) {
        const partes = conta.id.split('.');
        const ultimoSegmento = partes[partes.length - 1];
        if (!isNaN(Number(ultimoSegmento))) {
          idProdutoConta = parseInt(ultimoSegmento);
        }
      }

      // Filtrar pelo idProduto se fornecido
      if (props.idProduto && idProdutoConta !== props.idProduto) {
        return;
      }

      if (conta.historicoBloqueios) {
        conta.historicoBloqueios.forEach((h: any) => {
          const key = `BLOQUEIO-${h.dataBloqueio}-${h.loginBloqueio}-${h.justificativaBloqueio}`;
          if (!keys.has(key)) {
            keys.add(key);
            items.push({
              descricao: h.descricao,
              produto: h.produto,
              data: h.dataBloqueio,
              login: h.loginBloqueio,
              justificativa: h.justificativaBloqueio,
              tipoAcao: 'BLOQUEIO',
              descricaoMotivoAcao: h.descricaoMotivoAcao,
              idMotivoAcao: h.idMotivoAcao
            });
          }
        });
      }

      if (conta.historicoDesbloqueios) {
        conta.historicoDesbloqueios.forEach((h: any) => {
          const key = `DESBLOQUEIO-${h.dataDesbloqueio}-${h.loginDesbloqueio}-${h.justificativaDesbloqueio}`;
          if (!keys.has(key)) {
            keys.add(key);
            items.push({
              descricao: h.descricao,
              produto: h.produto,
              data: h.dataDesbloqueio,
              login: h.loginDesbloqueio,
              justificativa: h.justificativaDesbloqueio,
              tipoAcao: 'DESBLOQUEIO',
              descricaoMotivoAcao: h.descricaoMotivoAcao,
              idMotivoAcao: h.idMotivoAcao
            });
          }
        });
      }
    });

    // Ordenar por data: do mais atual para o mais antigo (descendente)
    return items.sort((a, b) => parseISO(b.data).getTime() - parseISO(a.data).getTime());
  }, [dadosBloqueio, props.idProduto]);

  function getIcon(item: HistoricoItem) {
    if (item.tipoAcao === 'BLOQUEIO') {
      return item.descricaoMotivoAcao?.toLowerCase().includes("indevido") ? MdBlock : MdLock;
    }
    return MdLockOpen;
  }

  function getColor(item: HistoricoItem) {
    if (item.tipoAcao === 'BLOQUEIO') {
      return "red.500";
    }
    return "green.500";
  }

  function getLabel(item: HistoricoItem) {
    const acao = item.tipoAcao === 'BLOQUEIO' ? "Bloqueio" : "Desbloqueio";
    const motivo = item.descricaoMotivoAcao ? ` (${item.descricaoMotivoAcao})` : "";
    return `${acao} ${item.descricao} ${motivo}`;
  }

  return (
    <Card flexDirection="column" w="100%" px="0px" mb="20px">
      <Flex align="center" w="100%" px="25px" py="10px">
        <Text fontSize="xl" fontWeight="bold">
          Histórico de Bloqueios
        </Text>
      </Flex>

      <Flex direction="column" w="100%" px="25px" pb="20px">
        {loading ? (
           <LoadingSpinner size="25px" />
        ) : historicoFiltrado.length > 0 ? (
          <TableContainer width="100%" maxH="240px" overflowY="auto">
            <Table variant="simple" size="sm">
              <Tbody>
                {historicoFiltrado.map((item, index) => (
                  <Tr key={index}>
                    <Td width="50px" border="none" py="10px">
                      <IconButton
                        aria-label="Status icon"
                        borderRadius="50%"
                        icon={<Icon as={getIcon(item)} w="20px" h="20px" color={getColor(item)} />}
                        variant="ghost"
                        isRound
                      />
                    </Td>
                    <Td border="none" py="10px">
                      <Text fontSize="sm" color="secondaryGray.900" fontWeight="500">
                        <b>{getLabel(item)}</b> às {format(
                          new Date(parseISO(item.data).getTime() - 3 * 60 * 60 * 1000),
                          "dd/MM/yyyy HH:mm:ss"
                        )}
                      </Text>

                      {/*  {item.justificativa && (*/}
                      {/*  <Text fontSize="xs" color="secondaryGray.600">*/}
                      {/*    Justificativa: {item.justificativa}*/}
                      {/*  </Text>*/}
                      {/*)}*/}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        ) : (
          <Text fontSize="md" color="gray.500" mt="10px">
            Nenhum histórico de bloqueio encontrado para este produto.
          </Text>
        )}
      </Flex>
    </Card>
  );
}
